import dayjs, { type Dayjs } from "dayjs";
import type { SchedulePick, ScheduleSlot } from "../../service/schedulePick.service";
import { LC_KEYS } from "../../utils/constants";
import { minutesSince } from "../CreateNoteModal/schedulePick.utils";

/** Rows the card shows before "Show N more". */
export const VISIBLE_SLOTS = 3;
/** The list covers everyone booked from an hour ago (clinics run late) to two hours ahead. */
export const LOOK_BACK_MINUTES = 60;
export const LOOK_AHEAD_MINUTES = 120;
/**
 * The three rows the card shows start half an hour back: Start is pressed a median of 19 minutes
 * after the booked slot (see ``client.schedule_pick``), so that is where "in the room" usually is.
 * Earlier appointments are above them once the list is expanded.
 */
export const OPENS_BACK_MINUTES = 30;

/**
 * What the Start card offers: never a guess at one patient. It asks, and each answer is one click.
 *
 * - ``list``: "Who's in the room?" over today's schedule from an hour ago to two hours ahead, in
 *   appointment order (see ``roomSlots``). The card shows three rows from ``first`` and expands
 *   to the whole list.
 * - ``dismissed``: the clinician closed the list (×). Start Encounter is the card, with Show,
 *   until the schedule moves on: the list comes back once its first visible appointment changes.
 * - ``none``: nobody booked in that span or no schedule today: the plain Start Encounter card.
 */
export type QuickStartView =
  | { kind: "list"; slots: ScheduleSlot[]; first: number }
  | { kind: "dismissed"; slots: ScheduleSlot[]; first: number }
  | { kind: "none" };

/** Today's undocumented appointments in the server's order: the Recommended group, then the rest of the day. */
export const upcomingSlots = (pick: SchedulePick | null | undefined): ScheduleSlot[] => {
  if (!pick || pick.state !== "ready") return [];
  return [...pick.recommended, ...pick.up_next].filter((slot) => slot.client.id != null);
};

/**
 * The rows under "Who's in the room?": everyone on today's schedule booked from an hour ago to
 * two hours ahead, by appointment time (same time: the clinician's own patients, then by name).
 * A patient who already has a recording today is listed like anyone else.
 */
export const roomSlots = (pick: SchedulePick | null | undefined, now: Dayjs = dayjs()): ScheduleSlot[] => {
  if (!pick || pick.state !== "ready") return [];
  const from = now.subtract(LOOK_BACK_MINUTES, "minute").valueOf();
  const until = now.add(LOOK_AHEAD_MINUTES, "minute").valueOf();
  return [...pick.recommended, ...pick.up_next, ...pick.documented]
    .filter((slot) => slot.client.id != null)
    .map((slot) => ({ slot, at: dayjs(slot.scheduled_at).valueOf() }))
    .filter(({ at }) => at >= from && at <= until)
    .sort(
      (a, b) =>
        a.at - b.at ||
        Number(a.slot.client.is_owned === false) - Number(b.slot.client.is_owned === false) ||
        (a.slot.client.name || "").localeCompare(b.slot.client.name || ""),
    )
    .map(({ slot }) => slot);
};

/** Where the card's three rows start: the first appointment from half an hour ago on, kept three from the end. */
export const firstVisibleIndex = (slots: ScheduleSlot[], now: Dayjs = dayjs()): number => {
  const opensAt = now.subtract(OPENS_BACK_MINUTES, "minute").valueOf();
  const index = slots.findIndex((slot) => dayjs(slot.scheduled_at).valueOf() >= opensAt);
  const last = Math.max(slots.length - VISIBLE_SLOTS, 0);
  return index < 0 ? last : Math.min(index, last);
};

export const quickStartView = (
  pick: SchedulePick | null | undefined,
  { dismissedId, now = dayjs() }: { dismissedId?: number | null; now?: Dayjs } = {},
): QuickStartView => {
  const slots = roomSlots(pick, now);
  if (slots.length === 0) return { kind: "none" };
  const first = firstVisibleIndex(slots, now);
  if (dismissedId != null && slots[first].client.id === dismissedId) return { kind: "dismissed", slots, first };
  return { kind: "list", slots, first };
};

/**
 * The first patient of the list the clinician closed (×), remembered for that clinic day. In
 * LC_KEYS, so logging out (or the session expiring) clears it: on a shared workstation the next
 * clinician never inherits it.
 */
export const loadDismissed = (day: string | null | undefined): number | null => {
  if (!day) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(LC_KEYS.QUICK_START_DISMISSED) || "null") as {
      day?: string;
      id?: unknown;
    } | null;
    return stored && stored.day === day && typeof stored.id === "number" ? stored.id : null;
  } catch {
    return null;
  }
};

// The card is mounted twice (sidebar and the narrow layout's tab) and the app may be open in
// two tabs: every copy follows the stored dismissal, so × and Show apply to all of them at once.
const dismissedListeners = new Set<() => void>();
export const subscribeDismissed = (listener: () => void): (() => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === LC_KEYS.QUICK_START_DISMISSED) listener();
  };
  dismissedListeners.add(listener);
  window.addEventListener("storage", onStorage);
  return () => {
    dismissedListeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

export const saveDismissed = (day: string, id: number | null): void => {
  if (id == null) localStorage.removeItem(LC_KEYS.QUICK_START_DISMISSED);
  else localStorage.setItem(LC_KEYS.QUICK_START_DISMISSED, JSON.stringify({ day, id }));
  for (const listener of Array.from(dismissedListeners)) listener();
};

/**
 * ``quick_start_card_shown``: what the card offered, the denominator for every Quick Start rate
 * (``quick_start_started`` / shown is the take rate). ``key`` names one offer, so it is sent once
 * per page load however often the schedule polls; a new first row, or a new view of the card, is
 * a new offer. Null until the schedule has loaded. Ids, counts and reasons only.
 */
export const cardShownEvent = (
  pick: SchedulePick | null | undefined,
  view: QuickStartView,
): { key: string; properties: Record<string, unknown> } | null => {
  if (!pick) return null;
  const slots = view.kind === "none" ? [] : view.slots;
  const head = view.kind === "none" ? null : view.slots[view.first];
  // Why the plain card: the sheet's own state (no schedule, stale), or nobody booked from an
  // hour ago to two hours ahead.
  const noneReason =
    view.kind !== "none" ? null : pick.state !== "ready" ? pick.no_pick_reason ?? pick.state : "nobody_booked_soon";
  return {
    key: `${pick.day}:${view.kind}:${head?.client.id ?? noneReason}`,
    properties: {
      view: view.kind,
      candidates: slots.length,
      // Rows for patients who already have a recording today (a start there is a second one).
      recorded: slots.filter((slot) => slot.documented_at != null).length,
      none_reason: noneReason,
      pick_reason: pick.suggestion?.reason ?? null,
      confidence: pick.suggestion?.confidence ?? null,
      slot_offset_min: head ? minutesSince(head.scheduled_at) : null,
      upcoming: upcomingSlots(pick).length,
      documented: pick.documented.length,
      schedule_size: pick.schedule_size,
    },
  };
};
